import { useEffect, useMemo, useReducer, useRef, useState } from "react";
import { filtersReducer, formatDate } from "../../../utility-functions";
import { Filters } from "../../dashboard/Filters";
import Button from "../../design/Button";
import classes from "./Templates.module.css";
import GTable from "../../design/components/GTable";
import { useNavigate } from "react-router-dom";
import { JService } from "../../api-service/ApiService";
import { enqueueSnackbar } from "notistack";
import { Box, Menu, MenuItem } from "@mui/material";
import { threeDotsIcon } from "../../../utility-functions/assetLoader";
import DeleteTemplateModal from "../Modals/DeleteTemplateModal";
import Tag from "../Components/Tag";

const filtersList = [
  {
    name: "Status",
    toggle: false,
    isSearch: false,
    payloadFor: "templateStatus",
  },
  {
    name: "Category",
    toggle: false,
    isSearch: false,
    payloadFor: "category",
  },
];

const initialState = {
  templateStatus: {
    all: [
      { id: "APPROVED", name: "Approved" },
      { id: "PENDING", name: "Pending" },
      { id: "REJECTED", name: "Rejected" },
      { id: "FLAGGED", name: "Flagged" },
      { id: "PAUSED", name: "Paused" },
      { id: "PENDING_DELETION", name: "Pending Deletion" },
      { id: "DISABLED", name: "Disabled" },
      { id: "IN_APPEAL", name: "In Appeal" },
      { id: "REINSTATED", name: "Reinstated" },
    ],
    selected: [],
  },
  category: {
    all: [
      { id: "MARKETING", name: "Marketing" },
      { id: "UTILITY", name: "Utility" },
      {id :"AUTHENTICATION", name: "Authentication"},
    ],
    selected: [],
  },
  currentSet: "",
  searchText: "",
  searchFields: [],
};

const isSameArr = (ol, ne) => {
  if (!Array.isArray(ol) || !Array.isArray(ne)) return false;
  try {
    return ol.every((o) => ne.includes(o)) && ol.length === ne.length;
  } catch (err) {
    return true;
  }
};


const Templates = () => {
  let previousFilters = useRef(null);
  const navigate = useNavigate();
  const selectedRowRef = useRef(null);
  const [availableData, dispatchAvailableData] = useReducer(
    filtersReducer,
    initialState
  );

  //States
  const [rows, setRows] = useState([]);

  const [dropdownAnchorEl, setDropdownAnchorEl] = useState(null);
  const [isDropdownVisible, setIsDropdownVisible] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showLoader, setShowLoader] = useState(false);
  const [downloadData, setDownloadData] = useState({
    headers: [["Template name"], ["Category"], ["Status"], ["Last edited"]],
    data: [],
    ids: [],
  });
  const [gTablePageNumber, setGTablePageNumber] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [pageData, setPageData] = useState({
    currentPage: undefined,
    nextUrl: undefined,
    previousUrl: undefined,
  });

  //Handlers
  const onSearch = () => {
    navigate(0);
  };

  const listAllTemplates = async (
    type = "fetch",
    pSize = pageSize || 10,
    pageNumberArg = 1
  ) => {
    setShowLoader(true);

    let url = `/api/v1/whatsapp/templates/?page=${
      type === "fetch"
        ? 1
        : gTablePageNumber > 0 && type === "filters-changed"
          ? 1
          : pageNumberArg
    }&page_size=${pSize}`;

    const _availableDataEmpty = isFiltersEmpty(availableData);
    if (!_availableDataEmpty) {
      if (availableData?.templateStatus?.selected?.length)
        url += `&status=${JSON.stringify(availableData?.templateStatus?.selected)}`;

      if (availableData?.category?.selected?.length) {
        url += `&category=${JSON.stringify(availableData?.category?.selected)}`;
      }

      if (availableData?.searchText?.trim()?.length !== 0) {
        url += `&search=${encodeURIComponent(
          availableData?.searchText?.trim()
        )}`;
      }

      if (type === "filters-changed" && gTablePageNumber > 0) {
        setGTablePageNumber(0);
        setPageData({
          currentPage: 1,
          nextUrl: undefined,
          previousUrl: undefined,
        });
      }
    }

    try {
      const response = await JService.get(url);
      if (!response?.success)
        throw new Error(
          response?.message || "Could not retrieve templates list"
        );
      setPageData({
        currentPage: ["fetch", "filters-changed"].includes(type)
          ? 1
          : pageNumberArg,
        nextUrl: response?.data?.next,
        previousUrl: response?.data?.previous,
        rowCount: response?.data?.count || 0,
      });
      let responseData = response?.data?.results;
      const formatted = responseData?.map((t) => ({
        ...t,
        category: initialState?.category?.all?.find(
          (item) => item.id === t.category)?.name || t.category,
        status: initialState?.templateStatus?.all?.find(
          (item) => item.id === t.status
        ).name,
      }));
      if (["fetch-more", "fetch-previous", "fetch-next"].includes(type)) {
        setRows((prev) => [...prev, ...formatted]);
      } else {
        setRows(formatted);
      }
    } catch (error) {
      enqueueSnackbar(error?.message || "Could not retrieve templates list", {
        variant: "error",
      });
    } finally {
      setShowLoader(false);
    }
  };

  function handlePageChange(newPageArg, pageDirectionArg) {
    if (pageDirectionArg && newPageArg > -1) {
      const type = "fetch".concat(pageDirectionArg);
      listAllTemplates(type, pageSize, newPageArg + 1);
    }
  }

  function handlePageSizeChange(val) {
    setPageSize(val);
    setGTablePageNumber(0);
    listAllTemplates("page-size-change", val, 1);
  }

  const handleFilterApply = () => {
    listAllTemplates();
  };

  const isFiltersEmpty = (arg) => {
    return (
      arg?.templateStatus?.selected?.length === 0 &&
      arg?.category?.selected?.length === 0 &&
      arg?.searchText.trim()?.length === 0
    );
  };

  const isFiltersChanged = (oldf, newf) => {
    if (oldf === null && isFiltersEmpty(newf)) {
      return false;
    }
    try {
      const isTemplateStatusSame = isSameArr(
        oldf?.templateStatus?.selected,
        newf?.templateStatus?.selected
      );
      if (!isTemplateStatusSame) {
        return true;
      }

      const isCategorySame = isSameArr(
        oldf?.category?.selected,
        newf?.category?.selected
      );
      if (!isCategorySame) {
        return true;
      }

      if (oldf?.searchText.trim() !== newf?.searchText.trim()) {
        return true;
      }

      return false;
    } catch (error) {
      console.error("error from isFiltersChanged ", error);
    }
  };

  const threeDotsClickHandler = (e, row) => {
    e.stopPropagation();
    selectedRowRef.current = row;

    setIsDropdownVisible(true);
    setDropdownAnchorEl(e.currentTarget || null);
  };

  const threeDotsAutoClose = (e) => {
    const path = e.composedPath()?.map((pathItem) => {
      let newPathItem = null;
      newPathItem =
        pathItem?.tagName?.toLowerCase() +
        (pathItem?.className ? "." + pathItem?.className : "");
      return newPathItem;
    });

    if (!path?.length || path[0]?.includes("div.MuiBackdrop-root")) {
      setIsDropdownVisible(false);
      setDropdownAnchorEl(null);
      return;
    }
  };

  const dropdownClickHandler = (e) => {
    const menuItemType = e.target.dataset.menuitemtype;
    if (menuItemType === "edit") {
      navigate("/w-template/create", {
        state: { ...(selectedRowRef?.current ?? {}), edit: true } || "",
      });
    } else if (menuItemType === "delete") {
      setShowDeleteModal(true);
    }

    setIsDropdownVisible(false);
    setDropdownAnchorEl(null);
  };

  //Effects
  useEffect(() => {
    listAllTemplates();
  }, []);

  useEffect(() => {
    if (isFiltersEmpty(availableData) && isFiltersEmpty(previousFilters.current)) {
      return;
    }
    if (isFiltersChanged(previousFilters.current, availableData)) {
      listAllTemplates("filters-changed", pageSize, 1);
    }
    previousFilters.current = structuredClone(availableData);
  }, [availableData]);

  useEffect(() => {
    if (isDropdownVisible) window.addEventListener("click", threeDotsAutoClose);
    else window.removeEventListener("click", threeDotsAutoClose);

    return () => {
      window.removeEventListener("click", threeDotsAutoClose);
    };
  }, [isDropdownVisible]);

  useEffect(() => {
    setDownloadData((prevState) => {
      let newIds = [];
      const newData = [];

      for (let i = 0; i < rows?.length; ++i) {
        let item = rows[i];
        if (!newIds?.includes(item?.id)) {
          newIds.push(item?.id);
          let temp = [
            item?.name,
            item?.category,
            item?.status,
            formatDate(item?.updated_at) ?? "-",
          ];
          newData.push(temp);
        }
      }
      return { ...prevState, data: newData, ids: newIds };
    });
  }, [rows]);

  // Columns
  const cols = useMemo(() => {
    return [
      {
        field: "name",
        headerName: "Template name",
        headerClassName: "user-table-header-user-name",
        minWidth: 350,
        flex: 0.23,
        renderCell: (params) => (
          <span className={classes.templateName}>{params?.value}</span>
        ),
      },
      {
        field: "category",
        headerName: "Category",
        minWidth: 350,
        flex: 0.23,
        renderCell: (params) => (
          <span className={classes.category}>{params?.value}</span>
        ),
      },
      {
        field: "status",
        headerName: "Status",
        minWidth: 350,
        flex: 0.23,
        renderCell: (params) => (
          <Tag quality={params?.row?.quality} name={params?.value} />
        ),
      },
      {
        field: "updated_at",
        headerName: "Last edited",
        minWidth: 350,
        flex: 0.23, 
        renderCell: (params) => (
          <span className={classes.msg}>
            {formatDate(params?.value) ?? "-"}
          </span>
        ),
      },
      {
        field: "logsEdit",
        headerName: "",
        minWidth: 60,
        flex: 0.08,
        cellClassName: "table-cell-edit",
        renderCell: (params) => {
          return (
            <Box>
              <div
                className={classes.threeDotsContainer}
                onClick={(e) => threeDotsClickHandler(e, params?.row)}
              >
                {threeDotsIcon()}
              </div>
            </Box>
          );
        },
      },
    ];
  }, []);

  return (
    <div className="whatsapp-screen-container">
      <div className={classes.filterContainer}>
        <Filters
          onApply={handleFilterApply}
          filtersList={filtersList}
          dispatchAvailableData={dispatchAvailableData}
          availableData={availableData}
          dateFilter={false}
          fromScreen="w-templates"
          style={{ marginTop: "0" }}
        />
        <Button
          buttonText="Create template"
          hierarchy="green"
          variant="contained"
          onClick={() => navigate("/w-template/create")}
        />
      </div>
      <GTable
        leftHeader={{
          filters: [],
          isSearch: true,
        }}
        dispatchAvailableData={dispatchAvailableData}
        availableData={availableData}
        rightHeader={{
          isRefresh: true,
          isDownload: true,
        }}
        download={{
          data: downloadData,
          body: null,
          filename:
            "frejun-whatsapp-templates-" +
            new Date().toLocaleString("en-US", {
              weekday: "long",
              year: "numeric",
              month: "long",
              day: "numeric",
              hour: "2-digit",
              minute: "2-digit",
              hour12: true,
            }),
        }}
        refreshHandler={listAllTemplates}
        rows={rows}
        fromScreen="w-templates"
        columns={cols}
        useMuiTable={true}
        className={classes.templatesGtable}
        isLoading={showLoader}
        additionalProps={{ bgHover: true }}
        onRowClick={(params) =>
          navigate("/w-template/create", {
            state: { ...(params?.row ?? {}), edit: true },
          })
        }
        onSearch={onSearch}
        rowCount={pageData.rowCount || 0}
        handlePageChange={handlePageChange}
        onPageSizeChange={handlePageSizeChange}
        rowsPerPage={pageSize}
        gTablePageNumber={gTablePageNumber}
        setGTablePageNumber={setGTablePageNumber}
        paginationMode="server"
      />
      <Menu
        sx={{
          "& .MuiList-root": { width: "175px" },
          "& .MuiPaper-root": { border: "1px solid var(--grey-200)" },
        }}
        open={isDropdownVisible}
        anchorEl={dropdownAnchorEl}
      >
        <MenuItem
          onClick={dropdownClickHandler}
          data-menuitemtype="edit"
          sx={{ cursor: "pointer" }}
        >
          &nbsp;&nbsp;Edit
        </MenuItem>
        <MenuItem
          onClick={dropdownClickHandler}
          data-menuitemtype="delete"
          sx={{ cursor: "pointer" }}
        >
          &nbsp;&nbsp;Delete
        </MenuItem>
      </Menu>
      <DeleteTemplateModal
        visible={showDeleteModal}
        closeModalHandler={() => setShowDeleteModal(false)}
        name={selectedRowRef?.current?.name}
        id={selectedRowRef?.current?.template_id}
        refreshTemplates={listAllTemplates}
      />
    </div>
  );
};

export default Templates;
